import { useState, useRef, useEffect } from 'react';
import { toUserZoned, fromUserZoned } from '../../../shared/lib/tzDate';
import { CalendlyDatePopover } from '../components/CalendlyDatePopover';
import { DurationPopover } from '../components/DurationPopover';
import { UserCell, UserSelectPopover } from '../components/UserCell';
import { getStageClass } from '../lib/taskGrid';

// Extraído de `ComponentTaskHoy.jsx` (02.1-b6-taskhoy-orchestrator.md, pasos D2+D4+D5) -- los 3
// popovers de celda de la tabla de tareas (fecha/hora Calendly, duración, selección de
// encargado/supervisor) MÁS el efecto compartido de "cerrar al hacer click afuera", que los
// toca a los 3 en un solo listener -- separarlo hubiera significado partir un efecto que ya
// depende de los 3 clusters de estado, así que se combinan en un solo hook desde el inicio (ver
// nota de riesgo del paso D5 en el plan).
//
// `handleToggleUserSelect` (selección de encargado) dispara la apertura del popover de duración
// -- por eso Calendly/Duration/UserSelect viven en el mismo hook, no en 3 separados: partirlos
// hubiera requerido pasar un callback cruzado entre hooks para la misma cadena de eventos que ya
// existía en el código original.
//
// NOTA: `activeTimePopoverTaskId`/`showCalendarPopover`/`calendarPopoverRef` son estado muerto
// preexistente (confirmado con grep antes de mover -- `activeTimePopoverTaskId` nunca se lee,
// solo se limpia a `null`; `showCalendarPopover` solo lo prende un `renderCalendarDays()` que el
// container nunca invoca). Se preservan verbatim, documentados, mismo criterio que `metrics`/
// `flowNodes` de `useTaskPipeline.js`.
export function useTaskCellPopovers({
    userTz, todayStr, systemUsers, usersToSelectFrom, updateTaskInDB,
    tasks, selectedCalendarDay, setSelectedCalendarDay,
}) {
    // Unified Calendly Popover State
    const [activeCalendlyPopoverTaskId, setActiveCalendlyPopoverTaskId] = useState(null);
    const [activeDurationPopoverTaskId, setActiveDurationPopoverTaskId] = useState(null);
    const durationPopoverRef = useRef(null);
    const [popoverDate, setPopoverDate] = useState(todayStr); // YYYY-MM-DD
    const [popoverDurationVal, setPopoverDurationVal] = useState('5');
    // Estado muerto preexistente (distinto de `customTimeUnit`, que sí se usa) -- ver nota de
    // cabecera.
    const [popoverDurationUnit, setPopoverDurationUnit] = useState('minutos');
    
    // Date conflict state
    const [dateConflictTaskId, setDateConflictTaskId] = useState(null);
    const [dateConflictMessage, setDateConflictMessage] = useState(null);

    // Custom Time Popover State (Missing variables fixed)
    const [activeTimePopoverTaskId, setActiveTimePopoverTaskId] = useState(null);
    const [customTimeNum, setCustomTimeNum] = useState('');
    const [customTimeUnit, setCustomTimeUnit] = useState('minutos');

    // For calendar month navigation inside popover
    const [popoverMonth, setPopoverMonth] = useState(toUserZoned(new Date(), userTz).getMonth());
    const [popoverYear, setPopoverYear] = useState(toUserZoned(new Date(), userTz).getFullYear());

    const userSelectPopoverRef = useRef(null);
    const [activeUserSelectTaskId, setActiveUserSelectTaskId] = useState(null);
    const [activeUserSelectField, setActiveUserSelectField] = useState(null); // 'assignee' | 'supervisor'
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [focusedUserOptionIdx, setFocusedUserOptionIdx] = useState(0);

    // Estado muerto preexistente -- ver nota de cabecera.
    const [showCalendarPopover, setShowCalendarPopover] = useState(false);
    const calendarPopoverRef = useRef(null);

    const handleOpenCalendlyPopover = (task) => {
        // Fase timezones: reusa `todayStr`/`toUserZoned` (componente) -- antes usaba
        // `.toISOString()` (siempre UTC) para "hoy", que podía mostrar el día equivocado según
        // la hora y el timezone real del usuario.
        const today = toUserZoned(new Date(), userTz);

        setActiveCalendlyPopoverTaskId(task.id);

        // Default a la fecha actual siempre si no hay fecha
        setPopoverDate(task.date || todayStr);
        setPopoverMonth(today.getMonth());
        setPopoverYear(today.getFullYear());

        // Leer la hora inicial
        let initialTimeStr = null;
        if (task.start_time) {
            const startD = new Date(task.start_time);
            const hrs = startD.getHours();
            const ampm = hrs >= 12 ? 'pm' : 'am';
            const h12 = hrs % 12 || 12;
            const pad = n => String(n).padStart(2, '0');
            initialTimeStr = `${pad(h12)}:00 ${ampm}`;
        }
        setPopoverDurationVal(initialTimeStr);
    };

    const handlePrevMonth = () => {
        if (popoverMonth === 0) {
            setPopoverMonth(11);
            setPopoverYear(popoverYear - 1);
        } else {
            setPopoverMonth(popoverMonth - 1);
        }
    };

    const handleNextMonth = () => {
        if (popoverMonth === 11) {
            setPopoverMonth(0);
            setPopoverYear(popoverYear + 1);
        } else {
            setPopoverMonth(popoverMonth + 1);
        }
    };

    const handleSaveCalendlyDetails = async (taskId, specificTimeStr = null, specificDateStr = null) => {
        const timeToSave = specificTimeStr !== null ? specificTimeStr : popoverDurationVal;
        const dateToSave = specificDateStr !== null ? specificDateStr : popoverDate;

        let startCol = undefined;
        let isoStartTime = undefined;
        let isoEndTime = undefined;

        if (timeToSave) {
            const match = timeToSave.match(/^(\d{2}):(\d{2})\s(am|pm)$/i);
            if (match) {
                let hrs = parseInt(match[1], 10);
                const mins = parseInt(match[2], 10);
                const ampm = match[3].toLowerCase();

                if (ampm === 'pm' && hrs < 12) hrs += 12;
                if (ampm === 'am' && hrs === 12) hrs = 0;

                const minutesFromEight = (hrs - 8) * 60 + mins;
                startCol = minutesFromEight / 30;

                if (dateToSave) {
                    // Fase timezones: yyyy/mm/dd/hrs/mins vienen del popover, interpretados como
                    // wall-clock-en-`userTz` -- `fromUserZoned` calcula el instante UTC real.
                    const [yyyy, mm, dd] = dateToSave.split('-');
                    const localD = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd), hrs, mins, 0);
                    isoStartTime = fromUserZoned(localD, userTz).toISOString();

                    const endLocalD = new Date(localD);
                    endLocalD.setHours(hrs + 1); // default 1 hour duration
                    isoEndTime = fromUserZoned(endLocalD, userTz).toISOString();
                }
            }
        }

        const updatePayload = { date: dateToSave };
        if (startCol !== undefined) {
            updatePayload.start_col = startCol;
        }
        if (isoStartTime) updatePayload.start_time = isoStartTime;
        if (isoEndTime) updatePayload.end_time = isoEndTime;

        // Check for overlaps before saving
        let hasConflict = false;
        if (isoStartTime && isoEndTime) {
            const newStart = new Date(isoStartTime).getTime();
            const newEnd = new Date(isoEndTime).getTime();
            hasConflict = tasks.some(t => {
                if (t.id === taskId) return false;
                if (!t.start_time || !t.end_time) return false;
                const tStart = new Date(t.start_time).getTime();
                const tEnd = new Date(t.end_time).getTime();
                // Exclusive overlap (one task ending exactly when another begins is fine)
                return newStart < tEnd && newEnd > tStart;
            });
        }

        if (hasConflict) {
            setDateConflictTaskId(taskId);
            setDateConflictMessage('La tarea esta sobre otra actividad...');
            setTimeout(() => {
                setDateConflictTaskId(null);
                setDateConflictMessage(null);
            }, 7000);
        } else {
            setDateConflictTaskId(null);
            setDateConflictMessage(null);
        }

        // Escritura optimista + API — `useUpdateTask` (`onMutate`) parcha la cache.
        await updateTaskInDB(taskId, updatePayload);

        // Close popover
        setActiveCalendlyPopoverTaskId(null);
    };

    // slice 02.1 · b.3 — el popover calendly de fecha/hora vive en `CalendlyDatePopover.jsx`;
    // acá quedan `popoverDate`/`popoverDurationVal`/`popoverMonth`/`popoverYear` + el guardado.
    const renderCalendlyPopover = (taskId, variant) => (
        <CalendlyDatePopover
            variant={variant}
            selectedTime={popoverDurationVal}
            onTimeSelect={(t) => { setPopoverDurationVal(t); handleSaveCalendlyDetails(taskId, t); }}
            selectedDate={popoverDate}
            month={popoverMonth}
            year={popoverYear}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            onDateSelect={(d) => { setPopoverDate(d); handleSaveCalendlyDetails(taskId, null, d); }}
        />
    );

    // slice 02.1 · b.3 — `UserCell`/`UserSelectPopover` viven en su propio archivo; acá quedan
    // el estado (qué celda está abierta) y `handleToggleUserSelect` (con side-effects).
    const handleOpenUserSelect = (task, field) => {
        if (activeUserSelectTaskId === task.id && activeUserSelectField === field) {
            setActiveUserSelectTaskId(null);
            setActiveUserSelectField(null);
        } else {
            setActiveUserSelectTaskId(task.id);
            setActiveUserSelectField(field);
            setUserSearchQuery('');
            setFocusedUserOptionIdx(0);
        }
    };

    const renderUserCell = (task, field) => (
        <UserCell
            value={task[field]}
            systemUsers={systemUsers}
            onToggle={() => handleOpenUserSelect(task, field)}
        />
    );

    const handleToggleUserSelect = (task, field, userNick) => {
        const currentVal = task[field] || '';
        let nicks = currentVal.split(',').map(s => s.trim()).filter(Boolean);
        if (nicks.includes(userNick)) {
            nicks = nicks.filter(n => n !== userNick);
        } else {
            nicks.push(userNick);
        }
        const newVal = nicks.join(', ');

        // Optimistic update
        updateTaskInDB(task.id, { [field]: newVal });

        if (field === 'assignee') {
            setActiveUserSelectTaskId(null);
            setActiveUserSelectField(null);
            setActiveDurationPopoverTaskId(task.id);
        }
    };

    const renderUserSelectPopover = (task, field) => (
        <UserSelectPopover
            value={task[field]}
            options={usersToSelectFrom}
            popoverRef={userSelectPopoverRef}
            searchQuery={userSearchQuery}
            onSearchChange={setUserSearchQuery}
            focusedIdx={focusedUserOptionIdx}
            onFocusedIdxChange={setFocusedUserOptionIdx}
            onToggleUser={(nick) => handleToggleUserSelect(task, field, nick)}
        />
    );

    // Close calendar and time popovers on outside click
    //
    // El popover de fecha (`.calendly-glass-popover`) se renderiza en DOS lugares con la MISMA
    // condición (`activeCalendlyPopoverTaskId === task.id`): la fila de la tabla y, si esa
    // misma tarea está abierta en el panel de detalle, también ahí -- ambos coexisten en el DOM
    // a la vez. Antes esto comparaba contra `calendlyPopoverRef.current`, una sola ref para dos
    // instancias posibles: la ref solo trackea la que ganó la asignación (la última en montar),
    // así que un mousedown real dentro de la OTRA instancia se leía como "click afuera" y cerraba
    // el popover a mitad del click -- el día bajo el cursor desaparecía antes del mouseup, dejando
    // lo que hubiera debajo (ej. el textarea de descripción) recibiendo el evento. No se puede
    // clickear ningún día que quede geométricamente sobre otro elemento una vez que el popover
    // "equivocado" se cierra. `.closest()` contra la clase compartida no depende de cuál de las
    // dos instancias haya ganado la ref.
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (activeCalendlyPopoverTaskId && !event.target.closest('.calendly-glass-popover')) {
                setActiveCalendlyPopoverTaskId(null);
            }
            if (activeDurationPopoverTaskId && durationPopoverRef.current && !durationPopoverRef.current.contains(event.target)) {
                setActiveDurationPopoverTaskId(null);
            }
            if (activeUserSelectTaskId && userSelectPopoverRef.current && !userSelectPopoverRef.current.contains(event.target)) {
                setActiveUserSelectTaskId(null);
                setActiveUserSelectField(null);
            }
            if (calendarPopoverRef.current && !calendarPopoverRef.current.contains(event.target)) {
                setShowCalendarPopover(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeUserSelectTaskId, activeCalendlyPopoverTaskId, activeDurationPopoverTaskId]);

    const renderDurationPopover = (task) => (
        <DurationPopover
            popoverRef={durationPopoverRef}
            customNum={customTimeNum}
            customUnit={customTimeUnit}
            onCustomNumChange={setCustomTimeNum}
            onCustomUnitChange={setCustomTimeUnit}
            onCustomSubmit={(e) => handleCustomTimeSubmit(e, task.id)}
            onPresetSelect={(preset) => {
                handleTimeSelect(task.id, preset);
                setActiveDurationPopoverTaskId(null);
            }}
        />
    );

    const handleTimeSelect = (taskId, timeValue) => {
        let durationMinutes = 30;
        const val = parseInt(timeValue) || 1;
        if (timeValue.includes('min')) durationMinutes = val;
        else if (timeValue.includes('hora')) durationMinutes = val * 60;
        else if (timeValue.includes('día')) durationMinutes = val * 24 * 60;
        else if (timeValue.includes('semana')) durationMinutes = val * 24 * 60 * 7;

        updateTaskInDB(taskId, { tiempo: timeValue, autoSchedule: true, duration_minutes: durationMinutes });
        setActiveTimePopoverTaskId(null);
        setActiveDurationPopoverTaskId(null);
    };

    const handleCustomTimeSubmit = (e, taskId) => {
        e.preventDefault();
        if (!customTimeNum.trim()) return;
        const timeValue = `${customTimeNum} ${customTimeUnit}`;
        handleTimeSelect(taskId, timeValue);
        setCustomTimeNum('');
    };

    // June 2026 Calendar days builder (June starts on Monday, 30 days)
    // NOTA: dead code preexistente -- ver nota de cabecera. Nunca se invoca desde el render del
    // container (confirmado con grep). Se preserva verbatim.
    const renderCalendarDays = () => {
        const days = [];
        for (let i = 1; i <= 30; i++) {
            const dateStr = `2026-06-${i < 10 ? '0' + i : i}`;
            const dayTasks = tasks.filter(t => t.date === dateStr);
            const isSelected = selectedCalendarDay === dateStr;
            const isToday = dateStr === '2026-06-01';

            days.push(
                <div
                    key={i}
                    className={`calendar-day-cell ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
                    onClick={(e) => {
                        setSelectedCalendarDay(dateStr);
                        setShowCalendarPopover(true);
                        e.stopPropagation();
                    }}
                >
                    <span className="day-number">{i}</span>
                    {dayTasks.length > 0 && (
                        <div className="day-dots-container">
                            {dayTasks.slice(0, 3).map((task, idx) => (
                                <span key={idx} className={`day-dot-led ${getStageClass(task.stage)}`}></span>
                            ))}
                            {dayTasks.length > 3 && <span className="day-dots-more">+</span>}
                        </div>
                    )}
                </div>
            );
        }
        return days;
    };

    // Calendar popover task list
    const selectedDayTasks = tasks.filter(t => t.date === selectedCalendarDay);

    return {
        activeCalendlyPopoverTaskId, handleOpenCalendlyPopover, renderCalendlyPopover,
        popoverDurationVal, setPopoverDurationVal, popoverDate, setPopoverDate,
        popoverMonth, popoverYear, handlePrevMonth, handleNextMonth, handleSaveCalendlyDetails,
        dateConflictTaskId, dateConflictMessage,

        activeDurationPopoverTaskId, setActiveDurationPopoverTaskId, durationPopoverRef,
        renderDurationPopover, customTimeNum, customTimeUnit,
        setCustomTimeNum, setCustomTimeUnit, handleCustomTimeSubmit, handleTimeSelect,

        activeUserSelectTaskId, activeUserSelectField, userSearchQuery, setUserSearchQuery,
        focusedUserOptionIdx, setFocusedUserOptionIdx, userSelectPopoverRef,
        handleOpenUserSelect, handleToggleUserSelect, renderUserCell, renderUserSelectPopover,
    };
}
